import { useCallback } from "react";
import { useNavigate } from "react-router";
import type { RoomGroupKey, RoomScope } from "../../shared/types";
import { api } from "../lib/api";
import { directPath, everyonePath } from "./places";

/** The group a screen outside Conversations hands to the Start a circle sheet, in navigation state. */
export interface CircleSeed {
  webinarId: string;
  scope: RoomScope;
  key: RoomGroupKey;
  session?: string;
}

export interface TalkNavState {
  circle?: CircleSeed;
  /** Start a circle for this room, with nobody chosen yet. */
  circleRoom?: string;
}

/** Opens a person's Direct thread from anywhere in the app, given their registration. */
export function useOpenDirect(): (registrantId: string) => Promise<void> {
  const navigate = useNavigate();
  return useCallback(
    async (registrantId: string) => {
      const { contactId } = await api.talk.directFor(registrantId);
      navigate(directPath(contactId));
    },
    [navigate],
  );
}

/** Opens Conversations on a room with the circle sheet already filled for one of its groups. */
export function useStartCircle(): (seed: CircleSeed) => void {
  const navigate = useNavigate();
  return useCallback(
    (seed: CircleSeed) => {
      const state: TalkNavState = { circle: seed };
      navigate(everyonePath(seed.webinarId), { state });
    },
    [navigate],
  );
}

/** Opens a room in Conversations, at `to`, with the Start a circle sheet open for it. */
export function useStartCircleIn(): (webinarId: string, to: string) => void {
  const navigate = useNavigate();
  return useCallback(
    (webinarId: string, to: string) => {
      const state: TalkNavState = { circleRoom: webinarId };
      navigate(to, { state });
    },
    [navigate],
  );
}
